// components/AppShell.jsx
// Unified app shell — single header with section tabs + context-sensitive sub-nav.
// Replaces both Layout.js (sidebar+topbar) and SchedulerLayout.jsx (header-only).

import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { fetchStaff, fetchClients, fetchNotifications, fetchIDDProgressSummary } from '../scheduling/services/schedData';
import { markAllNotifsRead, markNotifRead, setOutlookOptIn, reconcileOutlook } from '../scheduling/services/schedApi';
import { fetchPoolNavStatus } from '../scheduling/services/poolApi';
import { fetchMyReportAccess, recordSignInBeacon } from '../scheduling/services/reportsApi';
import { fetchReassignAccess } from '../reassign/services/reassignApi';
import { accessibleReports } from '../scheduling/utils/reportRegistry';
import NavBot from './NavBot';
import TokenLoginAwarder from './tokens/TokenLoginAwarder';
import BugReporter from './BugReporter';
import SuggestionReporter from './SuggestionReporter';
import TourHost from './tour/TourHost';
import { tourStore } from './tour/tourStore';
import SettingsModal from '../scheduling/components/SettingsModal';
import OutlookAnnounceModal from '../scheduling/components/OutlookAnnounceModal';
import SessionExpiredModal from './SessionExpiredModal';
import './AppShell.css';
import '../scheduling/Scheduler.css';

const KB_VIDEO_URL = 'https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/rprs_swapping_kb.apng';
// 'https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/kbWaving.mp4';

// The Botfield Suite hub — :3100 in local dev, the apex domain in production.
// Temporarily unused while the Home button is hidden (hub not prod-ready yet).
// function hubUrl() {
//   const h = window.location.hostname;
//   return (h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.local'))
//     ? 'http://localhost:3100'
//     : 'https://botfield.ai';
// }

// Section definitions
const SECTIONS = [
  { prefix: '/schedule', label: 'Scheduling', to: '/schedule/grid' },
  { prefix: '/reassign', label: 'Reassignments', to: '/reassign/dashboard' },
];

// Sub-nav per section
const SCHED_NAV = [
  { to: '/schedule/grid', label: 'Scheduler' },
  // Reporting absorbed the old standalone Capacity page (Trello #309). Its visibility
  // comes from the rprs.ReportAccess probe, not an inline isSuperUser check, so adding
  // a report with a wider audience needs no change here.
  { to: '/schedule/reporting', label: 'Reporting' },
  { to: '/schedule/dashboard', label: 'Dashboard' },
  { to: '/schedule/pool', label: 'Job Pools' },
  { to: '/schedule/data-integrity', label: 'Data Integrity' },
];
const SCHED_ADMIN = { to: '/schedule/admin', label: 'Admin' };

const REASSIGN_NAV = [
  { to: '/reassign/dashboard', label: 'Dashboard' },
];
const REASSIGN_ADMIN = { to: '/reassign/admin', label: 'Admin Config' };

// Transient caret bubble under a header button (feature-awareness nudge). Clicking
// the body opens the feature (onOpen); the × dismisses without opening (onDismiss).
function FeatureInviteBubble({ text, onOpen, onDismiss }) {
  return (
    <div
      className="app-feature-bubble"
      role="button"
      tabIndex={0}
      title="Click to open"
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <button
        className="app-feature-bubble-x"
        aria-label="Dismiss"
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
      >
        &times;
      </button>
      {text}
    </div>
  );
}

export default function AppShell() {
  const { username, email, displayName, isSuperUser, isDevSuperUser, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active section
  const activeSection = location.pathname.startsWith('/reassign') ? '/reassign' : '/schedule';

  // ── Staff resolution (for notifications + profile link) ──
  const [myStaffId, setMyStaffId] = useState(null);
  const [myStaffName, setMyStaffName] = useState(null);
  // Scoped reassignment-admin flag: staff whose DepartmentCode starts with
  // "ADMIN" (e.g. ADMINHR) get full reassignment powers WITHOUT the global
  // isSuperUser (which would also unlock scheduler-side tools). Resolved from
  // the caller's own Staff row below. See Trello #263.
  const [isReassignAdmin, setIsReassignAdmin] = useState(false);
  const [allStaff, setAllStaff] = useState([]);     // full active-staff list for the header search
  const [allClients, setAllClients] = useState([]); // full client list for the header search

  // ── Global header search (clients + staff) ──
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!ready) return;
    fetchStaff().then(staffData => {
      // Stage 28O: don't filter to IsActive here. The backend (queryStaff)
      // appends the caller's own Staff row to the response when they're
      // inactive (e.g. contractors with IsActive=0). Filtering client-side
      // would strip that row back out and break myStaffId / myStaffName
      // resolution for those users. The backend only includes inactive
      // staff via this caller-self path, so an unfiltered search is safe.
      const list = staffData || [];
      setAllStaff(list);
      let me = email && list.find(s => (s.Email || '').toLowerCase() === email.toLowerCase());
      if (!me) {
        const prefix = (email || username || '').split('@')[0].toLowerCase();
        if (prefix) me = list.find(s => (s.Email || '').toLowerCase().startsWith(prefix + '@'));
      }
      if (!me && (email || '').includes('*')) {
        // SWA masked the email (e.g. "tma*****@bmss.com") and the UserId cache
        // didn't have an entry yet (cold-start). Try the unmasked prefix; only
        // auto-resolve when exactly one staff matches.
        const unmaskedPrefix = email.split('@')[0].replace(/\*+/g, '').toLowerCase();
        if (unmaskedPrefix.length >= 2) {
          const candidates = list.filter(s =>
            (s.Email || '').toLowerCase().startsWith(unmaskedPrefix)
          );
          if (candidates.length === 1) me = candidates[0];
        }
      }
      if (me) { setMyStaffId(me.StaffId); setMyStaffName(me.Name); }
      // Reassignment-admin: DepartmentCode LIKE 'ADMIN%' (case-insensitive).
      setIsReassignAdmin(/^ADMIN/i.test(me?.DepartmentCode || ''));
    }).catch(() => {});
  }, [ready, email, username]);

  // ── Notifications ──
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const notifRef = useRef(null);
  const unreadCount = notifications.filter(n => !n.IsRead).length;

  // The bell popover is an UNREAD inbox, not a history feed (suggestion #10,
  // rkitchen: "I've seen the same 3 notifications for weeks"). It used to list the
  // 6 most recent regardless of read state, so read items sat there forever and
  // there was no signal that anything had been dealt with — /schedule/notifications
  // is where history lives.
  //
  // `sessionUnread` is the set of ids that were unread when the popover was OPENED.
  // Hovering marks an item read immediately, but it stays listed (dimmed) until the
  // popover closes. Dropping it on the spot would slide the next item up under a
  // stationary cursor and mark THAT read too, cascading the list away in one
  // gesture — the user never chose that.
  const [sessionUnread, setSessionUnread] = useState(() => new Set());
  const hoverTimers = useRef(new Map());

  // Mirror of `notifications` so openNotifDropdown can read the current list without
  // taking it as a dependency — it's referenced by the outside-click effect, and
  // depending on the array would re-register that listener on every poll tick.
  const notificationsRef = useRef(notifications);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);

  const openNotifDropdown = useCallback((open) => {
    setShowNotifDropdown(open);
    if (open) {
      setSessionUnread(new Set(
        notificationsRef.current.filter(n => !n.IsRead).map(n => n.NotifId)
      ));
    } else {
      // Cancel pending dwells so a timer can't fire against a closed popover.
      hoverTimers.current.forEach(t => clearTimeout(t));
      hoverTimers.current.clear();
      setSessionUnread(new Set());
    }
  }, []);

  // Hover-to-read, on a dwell rather than on entry. Without the delay, sweeping the
  // mouse down to "View all" marks every item it crosses as read without the user
  // having read anything.
  const NOTIF_HOVER_MS = 700;
  const startNotifHover = useCallback((notifId, isRead) => {
    if (isRead || hoverTimers.current.has(notifId)) return;
    const t = setTimeout(() => {
      hoverTimers.current.delete(notifId);
      markNotifRead(notifId).catch(() => {});
      setNotifications(prev => prev.map(n => (
        n.NotifId === notifId ? { ...n, IsRead: true, ReadAt: new Date().toISOString() } : n
      )));
    }, NOTIF_HOVER_MS);
    hoverTimers.current.set(notifId, t);
  }, []);

  const cancelNotifHover = useCallback((notifId) => {
    const t = hoverTimers.current.get(notifId);
    if (t) { clearTimeout(t); hoverTimers.current.delete(notifId); }
  }, []);

  // Unmount safety — a pending dwell must not fire into a torn-down tree.
  useEffect(() => {
    const timers = hoverTimers.current;
    return () => { timers.forEach(t => clearTimeout(t)); timers.clear(); };
  }, []);

  // What the popover lists: still-unread, plus anything read during THIS opening so
  // the list doesn't reflow under the cursor.
  const popoverNotifs = notifications
    .filter(n => !n.IsRead || sessionUnread.has(n.NotifId))
    .slice(0, 6);
  // 🐛 pulse: the user has an unread "bug resolved" status change. Derived from
  // the already-loaded notifications (no extra fetch). Opening the bug modal
  // marks those read so the pulse clears (other notif types are untouched).
  const hasUnseenBugResolved = notifications.some(n => n.NotifType === 'bug_resolved' && !n.IsRead);
  const dismissBugPulse = useCallback(() => {
    const unseen = notifications.filter(n => n.NotifType === 'bug_resolved' && !n.IsRead);
    if (unseen.length === 0) return;
    setNotifications(prev => prev.map(n => (n.NotifType === 'bug_resolved' && !n.IsRead) ? { ...n, IsRead: true } : n));
    Promise.all(unseen.map(n => markNotifRead(n.NotifId).catch(() => {}))).catch(() => {});
  }, [notifications]);
  // 💡 pulse: same pattern as the bug pulse, but for 'suggestion_updated' notices.
  // Opening the suggestion modal marks those read (the bug pulse is untouched).
  const hasUnseenSuggestionUpdate = notifications.some(n => n.NotifType === 'suggestion_updated' && !n.IsRead);
  const dismissSuggestionPulse = useCallback(() => {
    const unseen = notifications.filter(n => n.NotifType === 'suggestion_updated' && !n.IsRead);
    if (unseen.length === 0) return;
    setNotifications(prev => prev.map(n => (n.NotifType === 'suggestion_updated' && !n.IsRead) ? { ...n, IsRead: true } : n));
    Promise.all(unseen.map(n => markNotifRead(n.NotifId).catch(() => {}))).catch(() => {});
  }, [notifications]);

  const loadNotifications = useCallback(() => {
    if (!myStaffId) return;
    fetchNotifications(myStaffId).then(data => setNotifications(data || [])).catch(() => {});
  }, [myStaffId]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // ── Feature-awareness popups (💡 / 🐛) ──
  // One-time-per-session caret bubbles under the suggestion + bug buttons, fired
  // a beat after the navbot greeting, nudging users toward the feedback channels.
  // Mirrors the navbot greeting (sessionStorage guard + timed show). Sequential:
  // 💡 then 🐛 (the two buttons are too close for side-by-side bubbles). Clicking
  // a bubble opens that modal via an openSignal counter and ends the sequence.
  const [feedbackInvite, setFeedbackInvite] = useState(null); // null | 'suggestion' | 'bug'
  const [suggestOpenSignal, setSuggestOpenSignal] = useState(0);
  const [bugOpenSignal, setBugOpenSignal] = useState(0);
  const inviteTimersRef = useRef([]);
  const endInvite = useCallback(() => {
    inviteTimersRef.current.forEach(clearTimeout);
    inviteTimersRef.current = [];
    setFeedbackInvite(null);
  }, []);
  useEffect(() => {
    if (sessionStorage.getItem('feedback-invite-seen')) return;
    const INVITE_DELAY = 5000; // just after the navbot greeting (T+3.5s)
    const INVITE_EACH = 6000;  // each bubble's visible window
    const timers = inviteTimersRef.current;
    timers.push(setTimeout(() => {
      sessionStorage.setItem('feedback-invite-seen', '1');
      setFeedbackInvite('suggestion');
      timers.push(setTimeout(() => {
        setFeedbackInvite('bug');
        timers.push(setTimeout(() => setFeedbackInvite(null), INVITE_EACH));
      }, INVITE_EACH));
    }, INVITE_DELAY));
    return () => { timers.forEach(clearTimeout); inviteTimersRef.current = []; };
  }, []); // mount once

  // ── First-visit tour offer (?) ──
  // Once per browser (localStorage), after the greeting/feedback bubbles settle,
  // a one-time invite under the "?" button nudges new users to take the tour.
  const [tourInvite, setTourInvite] = useState(false);
  const tourInviteTimersRef = useRef([]);
  useEffect(() => {
    if (localStorage.getItem('rprs-tour-offered')) return;
    const timers = tourInviteTimersRef.current;
    timers.push(setTimeout(() => {
      localStorage.setItem('rprs-tour-offered', '1');
      setTourInvite(true);
      timers.push(setTimeout(() => setTourInvite(false), 12000));
    }, 14000)); // after the navbot greeting + 💡/🐛 invite sequence
    return () => { timers.forEach(clearTimeout); tourInviteTimersRef.current = []; };
  }, []); // mount once

  // ── Settings modal + Outlook first-run announcement (Trello #133) ──
  const ANNOUNCE_KEY = 'rprs-announce-outlook-v1';
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsHighlight, setSettingsHighlight] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [announceBusy, setAnnounceBusy] = useState(false);
  const [outlookOptIn, setOutlookOptInState] = useState(false);
  const announceGateRef = useRef(false);

  // Header owns the current user's opt-in state (kept in sync by the Settings modal
  // + the announcement); initialized from the resolved "me" staff row.
  useEffect(() => {
    const me = allStaff.find(s => s.StaffId === myStaffId);
    if (me) setOutlookOptInState(!!me.OutlookSyncOptIn);
  }, [allStaff, myStaffId]);

  // Show the Outlook announcement once per browser, only for users not already opted in.
  useEffect(() => {
    if (announceGateRef.current) return;
    if (!ready || !myStaffId) return;
    const me = allStaff.find(s => s.StaffId === myStaffId);
    if (!me) return;                                  // wait for staff resolution
    announceGateRef.current = true;                   // evaluate the gate exactly once
    if (localStorage.getItem(ANNOUNCE_KEY)) return;
    if (me.OutlookSyncOptIn) { localStorage.setItem(ANNOUNCE_KEY, '1'); return; }
    setTimeout(() => setAnnounceOpen(true), 1500);    // no cleanup — the shell stays mounted
  }, [ready, myStaffId, allStaff]);

  const dismissAnnounce = () => { localStorage.setItem(ANNOUNCE_KEY, '1'); setAnnounceOpen(false); };
  const enableFromAnnounce = async () => {
    setAnnounceBusy(true);
    try {
      if (myStaffId) {
        await setOutlookOptIn(myStaffId, true);
        setOutlookOptInState(true);
        reconcileOutlook([myStaffId]);                // fire-and-forget backfill
      }
    } catch { /* non-blocking */ }
    finally { localStorage.setItem(ANNOUNCE_KEY, '1'); setAnnounceBusy(false); setAnnounceOpen(false); }
  };
  const openSettingsFromAnnounce = () => {
    localStorage.setItem(ANNOUNCE_KEY, '1');
    setAnnounceOpen(false);
    setSettingsHighlight(true);
    setSettingsOpen(true);
  };

  // Close dropdown on outside click. Routed through openNotifDropdown so the
  // session set and any pending hover dwells are torn down too — closing by
  // clicking away must reset the list the same way closing by the bell does.
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) openNotifDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openNotifDropdown]);

  // Close the search dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load the client list once (for the header search). Staff comes from the
  // resolution effect above; clients aren't otherwise needed in the shell.
  useEffect(() => {
    if (!ready) return;
    fetchClients().then(d => setAllClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [ready]);

  // ── IDD progress (drives the Data Integrity tab pulse + subtab indicator) ──
  // Lightweight per-user summary keyed on the active cycle. Used here only to
  // decide whether to pulse the top-bar Data Integrity nav link; the IDD page
  // itself derives its own progress from the full job list.
  const [iddProgress, setIddProgress] = useState(null);
  const loadIDDProgress = useCallback(() => {
    if (!myStaffId) return;
    fetchIDDProgressSummary(myStaffId).then(setIddProgress).catch(() => {});
  }, [myStaffId]);
  useEffect(() => { loadIDDProgress(); }, [loadIDDProgress]);
  // Pulse when the active cycle has incomplete subtabs for this user. When
  // there's no active cycle / no assigned jobs / fully submitted, no pulse.
  const iddPulse = !!(iddProgress?.active
    && iddProgress.subtabs
    && Object.values(iddProgress.subtabs).some(v => v === false));

  // ── Sign-in beacon (Trello #309) ───────────────────────────────────────────
  // The only place in the app that records "this person opened RPRS". Fires once per
  // browser session from the shell, so it covers every route rather than depending on
  // which page someone lands on. Never throws and never blocks render — see
  // recordSignInBeacon.
  useEffect(() => { recordSignInBeacon(); }, []);

  // ── Reporting nav state (Trello #309) ──────────────────────────────────────
  // One probe answers "can this person open any report at all". It ANSWERS for
  // everyone rather than 403-ing (same contract as the pool's nav_status), so a
  // refusal can't be mistaken for an outage.
  //
  // Fetched once, not polled: report audiences change when someone edits
  // rprs.ReportAccess, which is rare and never mid-session. A stale answer costs at
  // most one page refresh, unlike the pool's availability dot which changes hourly.
  const [reportKeys, setReportKeys] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchMyReportAccess()
      .then(r => { if (!cancelled) setReportKeys(r?.reports || []); })
      .catch(() => { if (!cancelled) setReportKeys([]); });   // fail closed: no tab
    return () => { cancelled = true; };
  }, []);

  // Filters on the registry's `ready` flag too, so someone granted only unbuilt
  // reports doesn't get a nav link to an empty page.
  const hasAnyReport = accessibleReports(reportKeys).length > 0;

  // ── Reassignments access (v2 PR 1) ─────────────────────────────────────────
  // The server-resolved role from rprs.ReassignAccess (api/shared/reassignAccess.js), the same
  // answer every Reassignments API now enforces. Super-users and ADMIN* staff resolve as admin
  // there too, so OR-ing in the old client-side rule only matters while the probe is in flight.
  // Fetched once, like the report probe: access changes are rare and never mid-session.
  const [reassignAccess, setReassignAccess] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchReassignAccess()
      .then(r => { if (!cancelled) setReassignAccess(r?.access || { allowed: false }); })
      .catch(() => { if (!cancelled) setReassignAccess({ allowed: false }); });   // fail closed
    return () => { cancelled = true; };
  }, []);
  const canReassign = isSuperUser || isReassignAdmin || !!reassignAccess?.allowed;
  const canReassignAdmin = isSuperUser || isReassignAdmin || reassignAccess?.role === 'admin';
  // Legacy Reassignments (pages/Dashboard.js) has two tiers: "admin" (every task, Confirm All ->
  // PE) and everyone else (tasks routed to them, submit for review). Its admin tier is the
  // server's approver: api/rprsPE refuses PE writes below approver. So an approver or admin row
  // gets the admin tier; viewers and submitters get the routed view. Handed to Dashboard under
  // its existing outlet name, isReassignAdmin, so the legacy file needs no edit.
  const legacyReassignAdmin = isSuperUser || isReassignAdmin || ['approver', 'admin'].includes(reassignAccess?.role);

  // ── Job Pools nav state (Trello #257) ──────────────────────────────────────
  // Two visually distinct states, because they mean different things:
  //
  //   PULSE  — you were ASSIGNED work from a pool and haven't acknowledged it.
  //            This is the load-bearing one: RPRS hands a job over and then goes
  //            quiet, so the hand-off must be impossible to miss. Derived from
  //            unread 'pool_assignment' notifications, cleared when the Job Pools
  //            page marks them read (same shape as the bug/suggestion pulses).
  //   STEADY — you are currently checked in as available. Ambient status, not a
  //            demand for attention.
  //
  // A tiny nav_status read backs the steady state + tooltip; it deliberately
  // returns no job rows.
  const [poolStatus, setPoolStatus] = useState(null);
  const loadPoolStatus = useCallback(() => {
    if (!myStaffId) return;
    fetchPoolNavStatus().then(setPoolStatus).catch(() => {});
  }, [myStaffId]);
  // Two triggers, because the two states go stale for different reasons.
  //
  //   Your OWN actions (check in/out, change capacity, claim) — a 'pool:changed'
  //   event dispatched by poolApi, so the dot updates immediately instead of only
  //   after a hard refresh. Precedent: 'app:cold-start-retry' in schedApi.
  //
  //   SOMEONE ELSE's action (a manager assigning you work while you sit on another
  //   page) — no event can reach us, so ride the existing 60s notification poll.
  //   The assignment PULSE is notification-derived and already self-heals on that
  //   poll; this puts the availability dot on the same clock.
  useEffect(() => {
    loadPoolStatus();
    const onPoolChanged = () => { loadPoolStatus(); loadNotifications(); };
    window.addEventListener('pool:changed', onPoolChanged);
    const interval = setInterval(loadPoolStatus, 60000);
    return () => {
      window.removeEventListener('pool:changed', onPoolChanged);
      clearInterval(interval);
    };
  }, [loadPoolStatus, loadNotifications]);

  // Pilot gate. nav_status answers this even for people without access (every other
  // pool action 403s), so one call already in flight tells us whether to render any
  // pool entry point at all.
  //
  // Strict === true: until the probe returns, treat access as absent. Defaulting the
  // other way would flash the Job Pools link for every staffer on each page load and
  // then snatch it away.
  const poolAccess = poolStatus?.hasAccess === true;

  const poolAssignPulse = poolAccess
    && notifications.some(n => n.NotifType === 'pool_assignment' && !n.IsRead);
  const poolAvailable = poolAccess && !!poolStatus?.available;

  // Self-explaining hover text per state, so neither indicator is a mystery.
  const poolNavTitle = (() => {
    if (poolAssignPulse) return 'You’ve been assigned a job from a pool — click to view it';
    if (poolAvailable) {
      const h = poolStatus?.capacityHours;
      return `You’re marked available${h ? ` with ${Math.round(Number(h) * 100) / 100}h free` : ''} — managers can see you`;
    }
    if (poolStatus?.waiting > 0) {
      return `${poolStatus.waiting} job${poolStatus.waiting === 1 ? '' : 's'} waiting in your pool`;
    }
    return 'Job Pools';
  })();

  // ── Token earning (display shelved June 2026) ──
  // The visible token wallet (badge, modal, welcome/opt-out) was removed per the
  // COO decision to drop the day-to-day overhead. Earning attribution continues
  // (Cherion's commitment): server-side save handlers still emit idd_updated /
  // budget_set / nonbill_submit, and the invisible <TokenLoginAwarder> below
  // still fires daily_login into the ledger. There is simply no balance shown.

  // Stage 28D: Cold-start retry toast. Write helpers in schedApi.js dispatch
  // `app:cold-start-retry` when a 5xx/network error fires within ~5s of the
  // request start (likely a function spin-up, not a real backend failure).
  // Show a brief non-blocking toast so the user knows the retry is happening
  // instead of staring at a frozen UI for ~2s.
  const [coldStartToast, setColdStartToast] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      setColdStartToast({ label: e?.detail?.label || 'request', ts: Date.now() });
      setTimeout(() => setColdStartToast(null), 3500);
    };
    window.addEventListener('app:cold-start-retry', handler);
    return () => window.removeEventListener('app:cold-start-retry', handler);
  }, []);

  // Session-expiry takeover. utils/fetchInterceptor dispatches
  // `app:session-expired` exactly once when an /api/* call comes back as the
  // SWA-edge HTML 401 that means the ~8h auth cookie died while the tab stayed
  // open. Unlike the cold-start toast above this is BLOCKING and never clears:
  // every subsequent /api/* call is dead until the user signs back in, which
  // reloads the SPA. The 60s notification poller means this usually surfaces
  // before the user invests effort in work that can't save.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('app:session-expired', handler);
    return () => window.removeEventListener('app:session-expired', handler);
  }, []);

  // ── Profile photo ──
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    if (!email) return;
    setPhotoUrl(`/api/myPhoto?size=64x64&u=${encodeURIComponent(email)}`);
    setPhotoFailed(false);
  }, [email]);

  // Insurance only: by the time any /api/* call can 401, `ready` is already
  // true (every caller lives in this shell or its <Outlet>). Rendering the
  // modal here rather than bare null just means an expiry during the auth
  // handshake can't leave a blank page with no explanation. Identical to
  // `return null` while sessionExpired is false.
  if (!ready) return <SessionExpiredModal open={sessionExpired} />;

  const initials = (displayName || username || '??')
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Sub-nav items for active section. Stage 28J: Data Integrity is hidden
  // for non-admins when no active cycle exists. EmployeeProfile becomes the
  // year-round window into non-billable stats. Admins always see DI so they
  // can manage cycles + admin tooling. Stage 28K: also surface the tab when
  // the user has an unsubmitted NonBillBudget — so anyone receiving a
  // reminder email lands on a page where the nav tab is visible.
  // `subtabs.nonbill` is false when the row is missing OR in 'draft' state.
  const rawNav = activeSection === '/reassign' ? REASSIGN_NAV : SCHED_NAV;
  // Dev users too: QA sandbox cycles (IsQA) are tested by devs who may manage no jobs.
  const dataIntegrityVisible =
    isSuperUser
    || isDevSuperUser
    || iddProgress?.active === true
    || (iddProgress?.subtabs && iddProgress.subtabs.nonbill === false);
  const subNavItems = rawNav.filter(item =>
    (item.to !== '/schedule/data-integrity' || dataIntegrityVisible)
    // Reporting shows when the caller can open at least one report. Resolved from
    // rprs.ReportAccess (see reportAccess.js) rather than isSuperUser, so opening a
    // report to a wider audience is a data change, not a code change here.
    && (item.to !== '/schedule/reporting' || hasAnyReport)
    // Job Pools is in pilot: superusers + the access.allowed_staff list. Same shape as
    // the two hides above. The server blocks the feature regardless; this just stops
    // advertising it.
    && (item.to !== '/schedule/pool' || poolAccess)
  );
  const adminItem = activeSection === '/reassign' ? REASSIGN_ADMIN : SCHED_ADMIN;
  // Admin Config link: superusers on either surface, plus Reassignments admins (ADMIN* dept or
  // an admin row in rprs.ReassignAccess) on the reassign surface only.
  const adminLinkVisible = isSuperUser || (activeSection === '/reassign' && canReassignAdmin);
  // Reassignments: anyone the server grants a role. Today that is super-users + ADMIN* staff;
  // the production team is added at the v2 cutover.
  const visibleSections = SECTIONS.filter(sec => sec.prefix !== '/reassign' || canReassign);

  // Highlight the top-right profile chip (same bright-green as an active nav tab)
  // when viewing your OWN employee profile — not someone else's.
  const isOnMyProfile = myStaffId != null && location.pathname === `/schedule/employee/${myStaffId}`;

  return (
    <div className="app-root">
      {/* Daily-login token award — invisible, fire-and-forget. The wallet UI was
          shelved (June 2026) but earnings attribution continues, so this still
          posts daily_login to the ledger. No-op if the rule is inactive. */}
      <TokenLoginAwarder
        apiBaseUrl="/api/tokens"
        contextApp="rprs"
        ready={ready}
      />

      {/* ── Header ── */}
      <header className="app-header">
        {/* Return to the Botfield Suite hub. Temporarily hidden — hub not
            prod-ready yet. Restore this <a> (+ hubUrl above) when it is.
        <a
          href={hubUrl()}
          title="Return to the Botfield Suite hub"
          aria-label="Home"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginRight: 12, padding: '6px 12px', borderRadius: 999,
            border: '1px solid rgba(128,128,128,0.4)', textDecoration: 'none',
            color: 'inherit', fontSize: '0.85rem', fontWeight: 600,
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          ⌂ Home
        </a>
        */}
        {/* Brand */}
        <div className="app-header-brand">
          <div className="app-header-kb">
            {/*
            <video
              src={KB_VIDEO_URL}
              autoPlay loop muted playsInline
              className="app-header-kb-video"
            />
            */}
            <img
              src={KB_VIDEO_URL}
              alt="KB animated mascot"
              className="app-header-kb-video"
            />
          </div>
          <div className="app-header-brand-text">
            <span className="app-header-title">RPRS</span>
            <span className="app-header-subtitle">Right People. Right Seats.</span>
          </div>
        </div>

        <div className="app-header-divider" />

        {/* Section tabs */}
        <nav className="app-header-sections">
          {visibleSections.map(sec => (
            <NavLink
              key={sec.prefix}
              to={sec.to}
              className={() =>
                `app-header-section-link${activeSection === sec.prefix ? ' app-header-section-link--active' : ''}`
              }
            >
              {sec.label}
            </NavLink>
          ))}
        </nav>

        <div className="app-header-divider" />

        {/* Sub-nav */}
        <nav className="app-header-nav">
          {subNavItems.map(item => {
            // Pulse the Data Integrity link when this user has incomplete
            // work in the active cycle. Other links never pulse.
            const isPool = item.to === '/schedule/pool';
            // Job Pools pulses for an unacknowledged assignment — the same
            // attention-grabbing treatment the IDD link uses.
            const pulse = (item.to === '/schedule/data-integrity' && iddPulse)
              || (isPool && poolAssignPulse);
            // ...and carries a quieter steady dot while you're checked in available.
            // Never both: an unacknowledged assignment already means your
            // availability was cleared.
            const steady = isPool && poolAvailable && !poolAssignPulse;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                title={isPool ? poolNavTitle : undefined}
                className={({ isActive }) =>
                  `app-header-nav-link${isActive ? ' app-header-nav-link--active' : ''}${pulse ? ' app-header-nav-link--pulse' : ''}${steady ? ' app-header-nav-link--available' : ''}`
                }
              >
                {item.label}
                {steady && <span className="app-nav-avail-dot" aria-label="You are marked available" />}
              </NavLink>
            );
          })}
          {adminLinkVisible && (
            <NavLink
              to={adminItem.to}
              className={({ isActive }) =>
                `app-header-nav-link${isActive ? ' app-header-nav-link--active' : ''}`
              }
            >
              {adminItem.label}
            </NavLink>
          )}
        </nav>

        {/* Global search: clients + staff (click-through to profiles) */}
        <div className="sched-search" ref={searchRef}>
          <input
            className="sched-search-input"
            type="text"
            placeholder="Search clients or staff…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => { if (searchQuery) setShowSearchDropdown(true); }}
          />
          {showSearchDropdown && searchQuery.trim().length >= 1 && (() => {
            const q = searchQuery.trim().toLowerCase();
            const clients = allClients
              .filter(c => (c.Name || '').toLowerCase().includes(q) || (c.Code || '').toLowerCase().includes(q))
              .slice(0, 5);
            const staff = allStaff
              .filter(s => (s.Name || '').toLowerCase().includes(q))
              .slice(0, 5);
            const go = (path) => { setSearchQuery(''); setShowSearchDropdown(false); navigate(path); };
            return (
              <div className="sched-search-dropdown">
                {clients.length === 0 && staff.length === 0 && <div className="sched-search-empty">No matches</div>}
                {clients.length > 0 && <div className="sched-search-section">Clients</div>}
                {clients.map(c => (
                  <div key={`c${c.ClientId}`} className="sched-search-item" onClick={() => go(`/schedule/client/${c.ClientId}`)}>
                    <span className="sched-search-item-name">{c.Name}</span>
                    {c.Code && <span className="sched-search-item-meta">{c.Code}</span>}
                  </div>
                ))}
                {staff.length > 0 && <div className="sched-search-section">Staff</div>}
                {staff.map(s => (
                  <div key={`s${s.StaffId}`} className="sched-search-item" onClick={() => go(`/schedule/employee/${s.StaffId}`)}>
                    <span className="sched-search-item-name">{s.Name}</span>
                    {s.Level && <span className="sched-search-item-meta">{s.Level}</span>}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        <div className="app-header-spacer" />

        {/* Right-side action cluster: suggest · report bug · notifications */}
        <div className="app-header-actions">
          {/* Suggest an improvement */}
          <span className="app-action-wrap">
            <SuggestionReporter
              authState={{ email, username, displayName, isSuperUser }}
              contextApp="rprs"
              pulse={hasUnseenSuggestionUpdate}
              onOpen={dismissSuggestionPulse}
              openSignal={suggestOpenSignal}
            />
            {feedbackInvite === 'suggestion' && (
              <FeatureInviteBubble
                text="💡 Got an idea? Suggest an improvement here."
                onOpen={() => { endInvite(); setSuggestOpenSignal(n => n + 1); }}
                onDismiss={endInvite}
              />
            )}
          </span>

          {/* Report a bug */}
          <span className="app-action-wrap">
            <BugReporter
              authState={{ email, username, displayName, isSuperUser }}
              contextApp="rprs"
              pulse={hasUnseenBugResolved}
              onOpen={dismissBugPulse}
              openSignal={bugOpenSignal}
            />
            {feedbackInvite === 'bug' && (
              <FeatureInviteBubble
                text="🐛 Run into a bug? Report it here."
                onOpen={() => { endInvite(); setBugOpenSignal(n => n + 1); }}
                onDismiss={endInvite}
              />
            )}
          </span>

          {/* Take a tour / help */}
          <span className="app-action-wrap">
            <button
              className="app-help-btn"
              title="Take a tour"
              aria-label="Take a tour"
              onClick={() => { setTourInvite(false); tourStore.openMenu(); }}
            >
              ?
            </button>
            {tourInvite && (
              <FeatureInviteBubble
                text="👋 New here? Take a quick tour."
                onOpen={() => { setTourInvite(false); tourStore.openMenu(); }}
                onDismiss={() => setTourInvite(false)}
              />
            )}
          </span>

        {/* Notification Bell */}
        <div className="app-notif-wrap" ref={notifRef}>
          <button className="app-notif-bell" onClick={() => openNotifDropdown(!showNotifDropdown)}
            title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications — all caught up'}
            aria-label="Notifications">
            &#128276;
            {unreadCount > 0 && <span className="app-notif-badge">{unreadCount}</span>}
          </button>

          {showNotifDropdown && (
            <div className="app-notif-dropdown">
              <div className="app-notif-dropdown-header">
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  {unreadCount > 0 ? `Unread (${unreadCount})` : 'Notifications'}
                </span>
                {unreadCount > 0 && (
                  <button className="app-notif-mark-all"
                    title="Mark every notification as read"
                    onClick={async () => {
                      if (myStaffId) {
                        await markAllNotifsRead(myStaffId);
                        setNotifications(prev => prev.map(n => ({ ...n, IsRead: true })));
                      }
                    }}>&#10003; Mark all read</button>
                )}
              </div>
              {popoverNotifs.length > 0 && (
                <div className="app-notif-dropdown-list">
                  {popoverNotifs.map(n => (
                    <div key={n.NotifId}
                      className={`app-notif-item${n.IsRead ? ' app-notif-item--just-read' : ' app-notif-item--unread'}`}
                      title={n.IsRead ? 'Read' : 'Hover to mark as read'}
                      onMouseEnter={() => startNotifHover(n.NotifId, n.IsRead)}
                      onMouseLeave={() => cancelNotifHover(n.NotifId)}
                    >
                      <div className="app-notif-item-dot" style={{
                        background: n.NotifType === 'assignment' ? '#3B82F6' : n.NotifType === 'removal' ? '#EF4444' : n.NotifType === 'move' ? '#F59E0B' : n.NotifType === 'bug_resolved' ? '#6CC24A' : n.NotifType === 'suggestion_updated' ? '#EAB308' : '#9CA3AF'
                      }} />
                      <div style={{ flex: 1 }}>
                        <div className="app-notif-item-title">{n.Title}</div>
                        <div className="app-notif-item-body">{n.Body}</div>
                        <div className="app-notif-item-time">
                          {n.ActorName && <>{n.ActorName} &middot; </>}
                          {new Date(n.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                      {!n.IsRead && <div className="app-notif-item-unread-dot" />}
                    </div>
                  ))}
                </div>
              )}
              {/* Caught up: no list at all, just the way through to history. The old
                  popover showed read items here forever, which is what made it feel
                  like notifications could never be cleared. */}
              {popoverNotifs.length === 0 && (
                <div className="app-notif-empty">&#127881; You&rsquo;re all caught up</div>
              )}
              <div className="app-notif-dropdown-footer">
                <button onClick={() => { openNotifDropdown(false); navigate('/schedule/notifications'); }}
                  title="Open the full notification history, including ones you've already read">
                  View all past notifications &rarr;
                </button>
              </div>
            </div>
          )}
        </div>

          {/* Settings */}
          <span className="app-action-wrap">
            <button
              className="app-help-btn"
              title="Settings"
              aria-label="Settings"
              onClick={() => { setSettingsHighlight(false); setSettingsOpen(true); }}
            >
              ⚙
            </button>
          </span>
        </div>{/* /app-header-actions */}

        {/* User — click to view profile */}
        <div className={`app-header-user${isOnMyProfile ? ' app-header-user--active' : ''}`}
          style={{ cursor: myStaffId ? 'pointer' : 'default' }}
          onClick={() => myStaffId && navigate(`/schedule/employee/${myStaffId}`)}
          title={myStaffId ? 'View my profile' : ''}
        >
          <div className="app-header-avatar">
            {photoUrl && !photoFailed ? (
              <img src={photoUrl} alt="" onError={() => setPhotoFailed(true)}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            ) : initials}
          </div>
          <div className="app-header-user-info">
            <div className="app-header-user-name">{myStaffName || displayName || username}</div>
            {isSuperUser ? (
              <div className="app-header-user-role">Production Committee</div>
            ) : isReassignAdmin ? (
              <div className="app-header-user-role">Reassignments Admin</div>
            ) : null}
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="app-content">
        {/* poolAccess rides the outlet context so the Pool tab, the Send to Pool
            buttons and the pools page all read ONE answer from the nav_status probe
            this shell already makes — rather than each fetching its own. */}
        <Outlet context={{ refreshNotifications: loadNotifications, refreshIDDProgress: loadIDDProgress, isReassignAdmin: legacyReassignAdmin, reassignAccess, poolAccess }} />
      </main>

      {/* Stage 28D: cold-start retry toast. Renders briefly when a write
          helper retries after a likely cold-start 5xx. */}
      {coldStartToast && (
        <div className="app-cold-start-toast" role="status" aria-live="polite">
          <span className="app-cold-start-toast-spinner" />
          <span>Server warming up — retrying…</span>
        </div>
      )}

      {/* KB Navigator help widget — rides along on every page */}
      <NavBot staffId={myStaffId} staffName={myStaffName} isSuperUser={isSuperUser} />

      {/* Guided tour — chooser + spotlight overlay (driven by tourStore) */}
      <TourHost staffId={myStaffId} isSuperUser={isSuperUser} />

      {/* Settings modal (⚙️ gear) + one-time Outlook first-run announcement (Trello #133) */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setSettingsHighlight(false); }}
        staffId={myStaffId}
        optIn={outlookOptIn}
        onOptInChange={setOutlookOptInState}
        highlightOutlook={settingsHighlight}
      />
      <OutlookAnnounceModal
        open={announceOpen}
        busy={announceBusy}
        onEnable={enableFromAnnounce}
        onDismiss={dismissAnnounce}
        onOpenSettings={openSettingsFromAnnounce}
      />

      {/* Session-expiry takeover — blocking, not dismissible. Rendered last and
          at its own z-index tier (1300) so it sits above Settings, the Outlook
          announcement, and any open scheduling modal. */}
      <SessionExpiredModal open={sessionExpired} />
    </div>
  );
}
